/**
 * `akash.escrow.v1.MsgAccountDeposit` — adding funds to a running deployment.
 *
 * In deployment v1beta4 there is no `MsgDepositDeployment`; a top-up is an
 * escrow-module message against the deployment's escrow ACCOUNT, identified
 * as `{ scope: deployment, xid: "<owner>/<dseq>" }` — the string
 * `DeploymentID.ToEscrowAccountID()` builds (`node/deployment/v1/escrow.go`).
 *
 * `schema.json` pins this message's own three fields. The embedded
 * `akash.escrow.id.v1.Account` and the packed `Deposit.sources` are not in it,
 * so `escrow-vectors.json` holds bytes produced by the real Go `Marshal()`
 * (`tools/escrowref`), and `escrow.test.ts` asserts these encoders reproduce
 * them exactly.
 */

import type { GeneratedType } from '@cosmjs/proto-signing'
import { BinaryReader, BinaryWriter } from 'cosmjs-types/binary'

import { DEPOSIT_SOURCE_BALANCE, DEPOSIT_SOURCE_INVALID } from './deployment'

/** `akash.escrow.id.v1.Scope`. */
export const ESCROW_SCOPE_DEPLOYMENT = 1

export interface EscrowAccountID {
  scope: number
  xid: string
}

export interface MsgAccountDeposit {
  signer: string
  id: EscrowAccountID
  deposit: { amount: { denom: string; amount: string }; sources: number[] }
}

export const MsgAccountDeposit = {
  typeUrl: '/akash.escrow.v1.MsgAccountDeposit',

  encode(m: MsgAccountDeposit, w: BinaryWriter = BinaryWriter.create()): BinaryWriter {
    if (m.signer !== '') w.uint32(10).string(m.signer)

    const id = BinaryWriter.create()
    if (m.id.scope !== 0) id.uint32(8).int32(m.id.scope)
    if (m.id.xid !== '') id.uint32(18).string(m.id.xid)
    w.uint32(18).bytes(id.finish())

    const dep = BinaryWriter.create()
    const coin = BinaryWriter.create()
    if (m.deposit.amount.denom !== '') coin.uint32(10).string(m.deposit.amount.denom)
    if (m.deposit.amount.amount !== '') coin.uint32(18).string(m.deposit.amount.amount)
    dep.uint32(10).bytes(coin.finish())
    // Field 5, packed — the same shape MsgCreateDeployment's deposit uses.
    const sources = m.deposit.sources.filter((s) => s !== DEPOSIT_SOURCE_INVALID)
    if (sources.length > 0) {
      const packed = BinaryWriter.create()
      for (const s of sources) packed.uint32(s)
      dep.uint32(42).bytes(packed.finish())
    }
    w.uint32(26).bytes(dep.finish())
    return w
  },

  /** Partial, like MsgCreateDeployment's: the console never reads one off the wire. */
  decode(input: BinaryReader | Uint8Array, length?: number): MsgAccountDeposit {
    const r = input instanceof BinaryReader ? input : new BinaryReader(input)
    const end = length === undefined ? r.len : r.pos + length
    const m = MsgAccountDeposit.fromPartial({})
    while (r.pos < end) {
      const tag = r.uint32()
      switch (tag >>> 3) {
        case 1:
          m.signer = r.string()
          break
        default:
          r.skipType(tag & 7)
      }
    }
    return m
  },

  fromPartial(o: Partial<MsgAccountDeposit>): MsgAccountDeposit {
    return {
      signer: o.signer ?? '',
      id: { scope: o.id?.scope ?? 0, xid: o.id?.xid ?? '' },
      deposit: { amount: { denom: o.deposit?.amount.denom ?? '', amount: o.deposit?.amount.amount ?? '' }, sources: o.deposit?.sources ?? [] },
    }
  },
}

export const escrowRegistryTypes: ReadonlyArray<[string, GeneratedType]> = [
  [MsgAccountDeposit.typeUrl, MsgAccountDeposit as unknown as GeneratedType],
]

/** Add `amount` to the escrow account of deployment `owner/dseq`, paid from the signer's balance. */
export function accountDepositMessage(owner: string, dseq: bigint, amount: { denom: string; amount: string }) {
  return {
    typeUrl: MsgAccountDeposit.typeUrl,
    value: MsgAccountDeposit.fromPartial({
      signer: owner,
      id: { scope: ESCROW_SCOPE_DEPLOYMENT, xid: `${owner}/${dseq.toString()}` },
      deposit: { amount, sources: [DEPOSIT_SOURCE_BALANCE] },
    }),
  }
}
